import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { api, getDevIdentity, setDevIdentity, setTokenProvider } from './api.js';
import type { AppConfig, Me } from './types.js';

/**
 * Entra ID sign-in.
 *
 * Authentication only: the token establishes who the caller is, and the API's
 * own User table decides what they can do. That is the "SSO with in-app roles"
 * choice, and it means nothing here ever reads a role claim.
 *
 * In local development the API can be started with DEV_AUTH_BYPASS=true, in
 * which case this falls back to a header-based identity picker so all five
 * roles are testable without a tenant.
 */

interface AuthState {
  config: AppConfig;
  me: Me | null;
  status: 'loading' | 'signed-out' | 'signed-in' | 'error';
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<void>;
  /** Dev-mode identity switching; null in a real deployment. */
  devMode: boolean;
  devIdentity: string | null;
  setDevIdentity: (value: string | null) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('useMe called before sign-in completed');
  return me;
}

/** Convenience permission check, so components do not hand-roll `.includes`. */
export function useCan(): (permission: string) => boolean {
  const { me } = useAuth();
  return useCallback(
    (permission: string) => Boolean(me?.permissions.includes(permission)),
    [me],
  );
}

export function AuthProvider({
  config,
  children,
}: {
  config: AppConfig;
  children: ReactNode;
}): JSX.Element {
  const devMode = config.auth.devAuthBypass;

  const msal = useMemo<IPublicClientApplication | null>(() => {
    if (devMode || !config.auth.tenantId || !config.auth.clientId) return null;
    return new PublicClientApplication({
      auth: {
        clientId: config.auth.clientId,
        authority: `https://login.microsoftonline.com/${config.auth.tenantId}`,
        redirectUri: window.location.origin,
        // False, so MSAL does not bounce back to the originating page after a
        // redirect sign-in. Each of those bounces is another history entry, and
        // history is exactly what makes the back button land on Microsoft's
        // login endpoint -- which only accepts POST and shows AADSTS900561 to a
        // GET. Popup sign-in avoids the problem entirely; this covers the
        // fallback path.
        navigateToLoginRequestUrl: false,
      },
      cache: {
        // Session storage rather than local: an internal admin tool should not
        // leave a usable token behind in a closed browser.
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    });
  }, [config.auth.clientId, config.auth.tenantId, devMode]);

  const scopes = useMemo(
    () => (config.auth.apiScope ? [config.auth.apiScope] : ['User.Read']),
    [config.auth.apiScope],
  );

  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [devId, setDevId] = useState<string | null>(getDevIdentity());

  // Wire the token provider before any request is made.
  useEffect(() => {
    if (!msal) {
      setTokenProvider(async () => null);
      return;
    }
    setTokenProvider(async () => {
      const account = msal.getAllAccounts()[0];
      if (!account) return null;
      try {
        const result = await msal.acquireTokenSilent({ scopes, account });
        return result.accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          // The refresh token has expired or consent was revoked; a redirect is
          // the only way forward, and it must not happen mid-request silently.
          await msal.acquireTokenRedirect({ scopes, account });
          return null;
        }
        throw err;
      }
    });
  }, [msal, scopes]);

  const loadMe = useCallback(async () => {
    const profile = await api.get<Me>('/api/me');
    setMe(profile);
    setStatus('signed-in');
  }, []);

  const bootstrap = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      if (devMode) {
        await loadMe();
        return;
      }
      if (!msal) {
        setError(
          'Entra ID is not configured on the server and the development bypass is off, so nobody can sign in. Set ENTRA_TENANT_ID and ENTRA_WEB_CLIENT_ID.',
        );
        setStatus('error');
        return;
      }

      await msal.initialize();
      const redirect = await msal.handleRedirectPromise();
      if (redirect) {
        // Drop the auth fragment from the address bar once it has been consumed,
        // so a refresh does not try to process a response that is already spent.
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }
      const account: AccountInfo | null = redirect?.account ?? msal.getAllAccounts()[0] ?? null;

      if (!account) {
        setStatus('signed-out');
        return;
      }
      msal.setActiveAccount(account);
      await loadMe();
    } catch (err) {
      setError((err as Error).message);
      setStatus('error');
    }
  }, [devMode, loadMe, msal]);

  useEffect(() => {
    void bootstrap();
    // Re-running on devId change is intentional: switching the dev identity must
    // re-read /api/me so the whole UI reflects the new role.
  }, [bootstrap, devId]);

  /**
   * Redirect, not popup.
   *
   * Popup sign-in was tried to stop the back button landing on Microsoft's
   * login endpoint (AADSTS900561, which only accepts POST). It reached
   * blank.html with a valid code and then hung: the opening window never picked
   * the response up. Two candidate causes were ruled out -- the popup was
   * returning to a dedicated blank page, and COOP was relaxed to
   * same-origin-allow-popups -- and it still hung, so the cause is something
   * else in this browser or tenant configuration.
   *
   * Redirect is reverted to because it demonstrably works. The back-button
   * error is cosmetic and happens on Microsoft's page after a SUCCESSFUL
   * sign-in; a sign-in that hangs is not cosmetic. navigateToLoginRequestUrl is
   * still false and the auth fragment is still stripped after the response is
   * consumed, which trims the history entries that caused it.
   */
  const signIn = useCallback(async () => {
    if (!msal) return;
    await msal.loginRedirect({ scopes, prompt: 'select_account' });
  }, [msal, scopes]);

  const signOut = useCallback(async () => {
    if (devMode) {
      setDevIdentity(null);
      setDevId(null);
      return;
    }
    if (!msal) return;
    await msal.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, [devMode, msal]);

  const applyDevIdentity = useCallback((value: string | null) => {
    setDevIdentity(value);
    setDevId(value);
  }, []);

  const value: AuthState = {
    config,
    me,
    status,
    error,
    signIn,
    signOut,
    refreshMe: loadMe,
    devMode,
    devIdentity: devId,
    setDevIdentity: applyDevIdentity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
